'use client'

import { useTranslations } from 'next-intl'
import { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'

import NoSSR from './no-ssr'
import {
    StyledToast,
    StyledToastCloseButton,
    StyledToastViewport,
    ToastMessage,
} from '../styled/toasts'
import toastStore from '../../stores/toast-store'
import { Cross2Icon } from '@radix-ui/react-icons'
import * as Toast from '@radix-ui/react-toast'
import { Button, Flex, Theme } from '@radix-ui/themes'

import RelativeTimeDisplay from './relative-time-display'

export type ToastItemOptions = {
    title?: string
    titleKey?: string
    titleKeyParams?: Record<string, any>
    message?: string
    messageKey?: string
    type?: 'warning' | 'success' | 'error' | 'info'
    actions?: {
        variant?: 'solid' | 'surface' | 'outline' | 'soft'
        label: ReactNode
        action: () => boolean | void
    }[]
    timestamp?: Date
    timeout?: number
    hideTimestamp?: boolean
    visible?: boolean
    read?: boolean
}

export type ToastItem = ToastItemOptions & {
    id: string
    timestamp: Date
}

type TToastViewProps = {
    toast: ToastItem
}

// Module-level component: defining it inside Snackbar would give it a new identity on every render,
// so React would unmount/remount every toast (and restart its RelativeTimeDisplay interval) on each store change.
const ToastView = ({ toast }: TToastViewProps) => {
    const t = useTranslations('snackbar')
    const tGeneral = useTranslations()
    const { removeToast, hideToast, updateToast } = toastStore

    return (
        // The store drives visibility: Radix's own 5 s timer is disabled and every Radix-initiated close
        // (close button, swipe, Escape) is reported back to the store.
        <StyledToast open duration={Infinity} onOpenChange={open => !open && hideToast(toast.id)}>
            <ToastMessage $type={toast.type ?? 'info'}>
                <Flex direction="row" justify="between" gap="2">
                    <Flex direction="column" gap="2" width="calc(100% - 3rem)">
                        {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
                        {toast.titleKey && (
                            <Toast.Title>
                                {tGeneral(toast.titleKey as any, toast.titleKeyParams)}
                            </Toast.Title>
                        )}
                        <Toast.Description>
                            {toast.messageKey
                                ? tGeneral(toast.messageKey as any)
                                : toast.message}
                        </Toast.Description>
                    </Flex>
                    {(!toast.actions || toast.actions.length == 0) && (
                        <Toast.Close asChild aria-label={t('close')}>
                            <StyledToastCloseButton color="gray" variant="soft" radius="full">
                                <Cross2Icon />
                            </StyledToastCloseButton>
                        </Toast.Close>
                    )}
                </Flex>

                {!!toast.actions ? (
                    <Flex gap="2">
                        {toast.actions.map((action, idx) => (
                            <Button
                                color="gray"
                                variant={action.variant || 'solid'}
                                key={idx}
                                onClick={() => {
                                    try {
                                        if (action.action())
                                            return

                                        removeToast(toast.id)
                                    }
                                    catch (err) {
                                        if (err instanceof Error) {
                                            updateToast({
                                                title: t('unexpected-error'),
                                                message: err?.message || err.toString(),
                                                type: 'error',
                                            })
                                            removeToast(toast.id)
                                        }

                                        throw err // escalate further
                                    }
                                }}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </Flex>
                ) : (
                    !toast.hideTimestamp && (
                        <RelativeTimeDisplay size="1" timestamp={toast.timestamp} />
                    )
                )}
            </ToastMessage>
        </StyledToast>
    )
}

// a component of its own, so `document` is touched only when NoSSR actually renders it (never on the server)
const ToastViewportPortal = () =>
    createPortal(
        <Theme>
            <StyledToastViewport />
        </Theme>,
        document.body
    )

const Snackbar = () => {
    const { hideToast, useStore } = toastStore
    const toasts = useStore()

    useEffect(() => {
        const interval = setInterval(() => {
            const currentToasts = toastStore.get()
            const now = Date.now()

            Object.values(currentToasts).forEach((toast: ToastItem) => {
                if (toast.visible == false || toast.timeout == -1 || toast.type == "error")
                    return

                const timeout = toast.timeout ?? 5_000
                const elapsed = now - toast.timestamp.getTime()

                if (elapsed >= timeout)
                    hideToast(toast.id)
            })
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    return (
        <Toast.ToastProvider swipeDirection="right">
            {Object.entries(toasts)
                .filter(([, toast]) => toast.visible != false)
                .sort(
                    ([, toast1], [, toast2]) =>
                        toast1.timestamp.getTime() - toast2.timestamp.getTime()
                )
                .map(([key, toast]) => (
                    <ToastView key={key} toast={toast} />
                ))}
            {/* Portal keeps the viewport out of transformed/overflowing ancestors; the bare `Theme`
                re-applies the app's Radix Theme (inherited through context) to the portalled node. */}
            <NoSSR>
                <ToastViewportPortal />
            </NoSSR>
        </Toast.ToastProvider>
    )
}

export default Snackbar
